import ArrowForwardIcon from "@bmi-digital/components/icon/ArrowForward";
import TwoPaneCarousel, {
  Slide as TwoPaneCarouselSlide
} from "@bmi-digital/components/two-pane-carousel";
import { replaceSpaces, transformHyphens } from "@bmi-digital/components/utils";
import VerticalRoller, {
  Slide as VerticalRollerSlide
} from "@bmi-digital/components/vertical-roller";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { graphql } from "gatsby";
import React from "react";
import { useTheme } from "@mui/material/styles";
import BrandLogo from "../BrandLogo";
import { useSiteContext } from "../Site";
import { getCTA } from "../link/utils";
import useCarouselLabels from "../../hooks/useCarouselLabels";
import { LinkElement, SectionElement } from "../styles/CarouselSectionStyles";
import createImageProps from "../image/createImageProps";
import createVideoProps from "../video/createVideoProps";
import type { Theme } from "@mui/material/styles";
import type { PromoSlide } from "../Promo";
import type { PageInfoSlide } from "../PageInfo";
import type { LinkData as LinkData } from "../link/types";
import type { PayloadCarouselSection } from "../../types/payload";

type Slide = PromoSlide | PageInfoSlide;

export type CarouselSectionData = Pick<
  PayloadCarouselSection,
  "blockType" | "title" | "variant"
> & {
  title: string;
  variant: "horizontal" | "vertical";
  slides: readonly { value: Slide }[];
  link: readonly [LinkData] | null;
};

const parseSlides = (
  slides: readonly { value: Slide }[],
  countryCode: string,
  linkLabel: string,
  variant: string,
  theme: Theme
): (TwoPaneCarouselSlide | VerticalRollerSlide)[] => {
  return slides
    .filter((slide) => slide.value.title)
    .map((slide, index) => {
      const cta = getCTA(slide.value, countryCode, linkLabel);
      const brandLogoIcons =
        "brandLogo" in slide.value && slide.value.brandLogo ? (
          <BrandLogo
            brandName={slide.value.brandLogo}
            brandWhiteBox={variant === "vertical"}
            data-testid={`carousel-section-slide-brand-logo-${index}`}
          />
        ) : undefined;
      return {
        title: slide.value.title,
        brandIcon: brandLogoIcons,
        media: slide.value.featuredVideo
          ? createVideoProps(
              {
                ...slide.value.featuredVideo,
                className: "video-preview-image",
                "data-testid": `carousel-section-slide-video-${index}`
              },
              theme
            )
          : slide.value.featuredMedia
            ? createImageProps(
                {
                  ...slide.value.featuredMedia,
                  "data-testid": `carousel-section-slide-image-${index}`
                },
                theme
              )
            : undefined,
        description:
          "subtitle" in slide.value ? slide.value.subtitle : undefined,
        cta: cta ? { ...cta, children: slide.value.title } : undefined,
        rollerSelectorGTM: {
          id: "selector-card3",
          label: slide.value.title,
          action: "Selector - Cards"
        }
      };
    });
};

const CarouselSection = ({
  data: { slides, title, variant, link }
}: {
  data: CarouselSectionData;
}) => {
  const { countryCode, getMicroCopy } = useSiteContext();
  const theme = useTheme();

  const { previousSlideLabel, nextSlideLabel } = useCarouselLabels();

  return (
    <SectionElement
      backgroundColor={variant === "vertical" ? "pearl" : "white"}
      data-testid={`carousel-section-${replaceSpaces(title)}`}
    >
      {variant === "vertical" ? (
        <VerticalRoller
          title={title}
          slides={parseSlides(
            slides,
            countryCode,
            getMicroCopy(microCopy.PAGE_LINK_LABEL),
            variant,
            theme
          )}
          // ✅ Passed the correctly scoped variables
          previousAccessibilityLabel={previousSlideLabel}
          nextAccessibilityLabel={nextSlideLabel}
        />
      ) : (
        <TwoPaneCarousel
          slides={parseSlides(
            slides,
            countryCode,
            getMicroCopy(microCopy.PAGE_LINK_LABEL),
            variant,
            theme
          )}
          // ✅ Passed the correctly scoped variables
          previousAccessibilityLabel={previousSlideLabel}
          nextAccessibilityLabel={nextSlideLabel}
        />
      )}
      {link && link[0] && (
        <LinkElement
          data={link[0]}
          endIcon={
            <ArrowForwardIcon
              data-testid={"carousel-section-arrow-forward-icon"}
            />
          }
          data-testid={"carousel-section-link"}
        >
          {transformHyphens(link[0].label)}
        </LinkElement>
      )}
    </SectionElement>
  );
};

export default CarouselSection;

export const query = graphql`
  fragment CarouselSectionFragment on PayloadCarouselSection {
    blockType
    title
    variant
    slides {
      ... on PayloadPromoOrPage {
        value {
          ...PromoSlideFragment
          ...PageInfoSlideFragment
        }
      }
    }
    link {
      ...LinkFragment
    }
  }
`;
